import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Layers, Send } from 'lucide-react';
import apiService from '../../services/apiService';
import { ADJUSTMENT_YEARS } from './forecastAdjustments';

// OP_09A — Bucketed allocation review for an aggregate forecasting session.
//
// Forecast inputs are grouped into five spread buckets, each with a pre-mapped
// default driver (allocation basis). Dollar levers and overlays pro-rate by the
// bucket's weights; growth points and percent assumptions copy to every unit;
// hire cohorts are assigned whole using the balance bucket's weights.

const MM = 1e6;

const BASES = [
  { id: 'balances', label: 'Loans + deposits' },
  { id: 'loans', label: 'Loans' },
  { id: 'deposits', label: 'Deposits' },
  { id: 'fees', label: 'NIR base' },
  { id: 'equal', label: 'Equal' }
];

// Lever → bucket map. Levers not listed (growth pts, workforce %) copy as-is.
const BUCKETS = [
  {
    id: 'balances',
    label: 'Loan & deposit balances',
    defaultBasis: 'balances',
    driverNote: 'Driver: planning-entity balance share — production capacity follows the existing book.',
    scaledLevers: [],
    copiedLevers: ['loanGrowthPts', 'depositGrowthPts'],
    carriesHires: true
  },
  {
    id: 'nii',
    label: 'Interest income, interest expense & FTP',
    defaultBasis: 'loans',
    driverNote: 'Driver: loan share — NII delta is modeled, not a direct input; allocation shown for FTP reporting.',
    scaledLevers: [],
    copiedLevers: [],
    informational: 'niiDelta'
  },
  {
    id: 'nir',
    label: 'Non-interest revenue (NIR)',
    defaultBasis: 'fees',
    driverNote: 'Driver: NIR baseline share — fee adjustments and driver overlays land where the fee book lives.',
    scaledLevers: ['feeAdjMM'],
    copiedLevers: [],
    carriesOverlays: true
  },
  {
    id: 'nie',
    label: 'Non-interest expense (NIE)',
    defaultBasis: 'balances',
    driverNote: 'Driver: balance share (size proxy) — workforce % assumptions copy to every unit.',
    scaledLevers: ['expenseAdjMM', 'compAdjMM'],
    copiedLevers: []
  },
  {
    id: 'credit',
    label: 'Provision for credit losses',
    defaultBasis: 'loans',
    driverNote: 'Driver: loan share — provision overlays follow loan exposure.',
    scaledLevers: ['provisionAdjMM'],
    copiedLevers: []
  }
];

const LEVER_LABELS = {
  loanGrowthPts: 'Loan growth pts',
  depositGrowthPts: 'Deposit growth pts',
  feeAdjMM: 'Fee adj $MM',
  expenseAdjMM: 'Expense adj $MM',
  compAdjMM: 'Comp adj $MM',
  provisionAdjMM: 'Provision adj $MM'
};

const LEVER_TO_BUCKET = {};
BUCKETS.forEach((bucket) => bucket.scaledLevers.forEach((lever) => { LEVER_TO_BUCKET[lever] = bucket.id; }));

const baseValue = (unit, basis) => {
  if (basis === 'equal') return 1;
  if (basis === 'loans') return unit.loansBase / MM;
  if (basis === 'deposits') return unit.depositsBase / MM;
  if (basis === 'fees') return (unit.feeBase || 0) / MM || (unit.loansBase + unit.depositsBase) / MM;
  return (unit.loansBase + unit.depositsBase) / MM;
};

const ForecastAllocation = ({
  units,
  annualResults,
  yearAdjustments,
  workforceYearInputs,
  feeDrivers,
  hireRows,
  marketInputs,
  workforceInputs,
  expenseInputs,
  shocks,
  scenario,
  macroMode,
  lineage,
  constraintSummary,
  scenarioVersion,
  actor
}) => {
  const navigate = useNavigate();
  const [bucketBasis, setBucketBasis] = useState(
    () => Object.fromEntries(BUCKETS.map((bucket) => [bucket.id, bucket.defaultBasis]))
  );
  const [overrides, setOverrides] = useState({});       // `${bucketId}:${unitId}` -> raw weight
  const [expandedBucket, setExpandedBucket] = useState({ nir: false });
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  // Per-bucket normalized weights.
  const bucketWeights = useMemo(() => {
    const result = {};
    BUCKETS.forEach((bucket) => {
      const basis = bucketBasis[bucket.id];
      const raw = units.map((unit) => {
        const overrideKey = `${bucket.id}:${unit.id}`;
        if (overrides[overrideKey] !== undefined) return Math.max(0, Number(overrides[overrideKey]) || 0);
        return baseValue(unit, basis);
      });
      const total = raw.reduce((sum, value) => sum + value, 0) || 1;
      result[bucket.id] = units.map((unit, index) => ({
        unit, raw: raw[index], weight: raw[index] / total
      }));
    });
    return result;
  }, [units, bucketBasis, overrides]);

  const weightFor = (bucketId, unitId) =>
    bucketWeights[bucketId].find((entry) => entry.unit.id === unitId)?.weight || 0;

  // Hire assignment uses the balance bucket's weights.
  const hireAssignment = useMemo(() => {
    const weights = bucketWeights.balances;
    const filled = Object.fromEntries(units.map((unit) => [unit.id, 0]));
    const totalFte = hireRows.reduce((sum, row) => sum + (Number(row.fte) || 0), 0) || 1;
    const assignment = {};
    [...hireRows]
      .sort((a, b) => (Number(b.fte) || 0) - (Number(a.fte) || 0))
      .forEach((row) => {
        const target = weights.reduce((best, candidate) =>
          (candidate.weight - filled[candidate.unit.id] > best.weight - filled[best.unit.id] ? candidate : best)
        );
        assignment[row.id] = target.unit.id;
        filled[target.unit.id] += (Number(row.fte) || 0) / totalFte;
      });
    return assignment;
  }, [hireRows, bucketWeights, units]);

  // Aggregate per-FY amounts being allocated, by bucket.
  const overlayTotalsByYear = useMemo(() => {
    const totals = ADJUSTMENT_YEARS.map(() => 0);
    feeDrivers.forEach((driver) => (driver.overlays || []).forEach((overlay) => {
      const yearIndex = Math.min(4, Math.floor((overlay.monthIndex || 0) / 12));
      totals[yearIndex] += Number(overlay.amountMM) || 0;
    }));
    return totals;
  }, [feeDrivers]);

  const bucketAggregate = useMemo(() => {
    const result = {};
    BUCKETS.forEach((bucket) => {
      if (bucket.informational === 'niiDelta') {
        result[bucket.id] = annualResults.map((row) => row.niiDelta || 0);
        return;
      }
      const totals = ADJUSTMENT_YEARS.map((_, yearIndex) =>
        bucket.scaledLevers.reduce((sum, lever) => sum + (yearAdjustments[lever]?.[yearIndex] || 0), 0)
        + (bucket.carriesOverlays ? overlayTotalsByYear[yearIndex] : 0)
      );
      result[bucket.id] = totals;
    });
    return result;
  }, [annualResults, yearAdjustments, overlayTotalsByYear]);

  const fy1 = annualResults[0] || {};

  const pushDown = async () => {
    setPushing(true);
    setPushResult(null);
    const errors = [];
    for (const unit of units) {
      const scaledAdjustments = Object.fromEntries(
        Object.entries(yearAdjustments).map(([lever, values]) => {
          const bucketId = LEVER_TO_BUCKET[lever];
          if (!bucketId) return [lever, values];   // growth pts copy as-is
          const weight = weightFor(bucketId, unit.id);
          return [lever, values.map((value) => Number((value * weight).toFixed(2)))];
        })
      );
      const nirWeight = weightFor('nir', unit.id);
      const scaledDrivers = feeDrivers.map((driver) => ({
        ...driver,
        overlays: (driver.overlays || []).map((overlay) => ({
          ...overlay,
          amountMM: Number(((Number(overlay.amountMM) || 0) * nirWeight).toFixed(3)),
          note: overlay.note ? `${overlay.note} (allocated ${(nirWeight * 100).toFixed(0)}%)` : overlay.note
        }))
      }));
      const unitHires = hireRows
        .filter((row) => hireAssignment[row.id] === unit.id)
        .map((row) => ({ ...row, name: `${row.name || 'Hire'} · from aggregate` }));
      const weightSummary = BUCKETS
        .map((bucket) => `${bucket.id} ${(weightFor(bucket.id, unit.id) * 100).toFixed(0)}%`)
        .join(', ');
      const allocationNote = {
        key: `alloc-${Date.now()}`,
        field: 'Allocated from aggregate session',
        priorValue: '·',
        newValue: `Bucketed spread of ${units.length}-unit aggregate: ${weightSummary}`,
        user: actor,
        timestamp: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
        source: 'Aggregate allocation'
      };
      const balanceWeight = weightFor('balances', unit.id);
      try {
        await apiService.saveForecastSubmission(unit.id, {
          actor,
          inputs: {
            marketInputs,
            workforceInputs,
            expenseInputs,
            shocks,
            scenario,
            macroMode,
            workforceYearInputs,
            yearAdjustments: scaledAdjustments,
            feeDrivers: scaledDrivers,
            hireRows: unitHires,
            lineage: { ...lineage, [allocationNote.key]: allocationNote }
          },
          modelSnapshot: {
            capturedAt: new Date().toISOString(),
            scenarioVersion: `${scenarioVersion}_ALLOC`,
            allocatedFromAggregate: true,
            allocationWeights: Object.fromEntries(
              BUCKETS.map((bucket) => [bucket.id, Number(weightFor(bucket.id, unit.id).toFixed(4))])
            ),
            annualResults: annualResults.map((row) => ({
              ...row,
              baseline: row.baseline * balanceWeight,
              scenarioBase: row.scenarioBase * balanceWeight,
              adjusted: row.adjusted * balanceWeight,
              delta: row.delta * balanceWeight,
              rateVolumeVariance: row.rateVolumeVariance * weightFor('nii', unit.id),
              feeVariance: row.feeVariance * weightFor('nir', unit.id),
              expenseVariance: row.expenseVariance * weightFor('nie', unit.id),
              creditVariance: row.creditVariance * weightFor('credit', unit.id),
              taxVariance: row.taxVariance * balanceWeight,
              niiDelta: row.niiDelta * weightFor('nii', unit.id)
            })),
            constraintSummary: {
              green: constraintSummary.green, yellow: constraintSummary.yellow, red: constraintSummary.red
            },
            overrides: Object.keys(lineage).length + 1,
            blockedRows: 0
          }
        });
      } catch (error) {
        errors.push(`${unit.division} · ${unit.market}: ${error?.response?.data?.error || error.message}`);
      }
    }
    setPushing(false);
    setPushResult(errors.length ? { tone: 'error', message: errors.join(' | ') } : { tone: 'ok' });
  };

  const formatCell = (bucket, value) => {
    if (bucket.id === 'balances') return null; // handled separately
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
  };

  return (
    <section className="forecast-panel forecast-sticky-section" id="forecast-submit">
      <details className="forecast-accordion" open>
        <summary>
          <div>
            <span className="forecast-section-code">OP_09A · Allocate & push down</span>
            <h2>Spread the aggregate forecast to {units.length} business units, bucket by bucket</h2>
          </div>
          <Layers size={18} />
        </summary>

        <div className="forecast-accordion-body">
          <div className="forecast-submit-chips">
            <span className="forecast-health-chip neutral">
              Aggregate {fy1.year} Δ {fy1.delta >= 0 ? '+' : ''}{(fy1.delta || 0).toFixed(1)}MM
            </span>
            <span className="forecast-health-chip neutral">{hireRows.length} hire cohorts · assigned via balance share</span>
            <span className="forecast-health-chip neutral">Growth pts & workforce % copy to every unit</span>
          </div>

          {BUCKETS.map((bucket) => {
            const weights = bucketWeights[bucket.id];
            const aggregate = bucketAggregate[bucket.id];
            const isOpen = expandedBucket[bucket.id];
            const hasDollars = bucket.scaledLevers.length > 0 || bucket.informational;
            return (
              <div className="forecast-subpanel alloc-bucket" key={bucket.id}>
                <div className="alloc-bucket-head">
                  <button
                    type="button" className="hire-expand"
                    onClick={() => setExpandedBucket((previous) => ({ ...previous, [bucket.id]: !previous[bucket.id] }))}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <div className="alloc-bucket-title">
                    <strong>{bucket.label}</strong>
                    <span>{bucket.driverNote}</span>
                  </div>
                  <span className="forecast-segmented alloc-basis">
                    {BASES.map((candidate) => (
                      <button
                        type="button" key={candidate.id}
                        className={bucketBasis[bucket.id] === candidate.id ? 'active' : ''}
                        onClick={() => setBucketBasis((previous) => ({ ...previous, [bucket.id]: candidate.id }))}
                      >
                        {candidate.label}
                      </button>
                    ))}
                  </span>
                </div>

                <div className="alloc-bucket-summary forecast-mono">
                  {bucket.id === 'balances' ? (
                    <>
                      {['loanGrowthPts', 'depositGrowthPts'].map((lever) => (
                        <span key={lever}>
                          {LEVER_LABELS[lever]}: {ADJUSTMENT_YEARS.map((year, yearIndex) =>
                            `${yearAdjustments[lever]?.[yearIndex] >= 0 ? '+' : ''}${(yearAdjustments[lever]?.[yearIndex] || 0).toFixed(1)}`
                          ).join(' / ')} (copies)
                        </span>
                      ))}
                      <span>Hires: {hireRows.length} cohorts assigned whole</span>
                    </>
                  ) : (
                    <span>
                      {bucket.informational ? 'Modeled NII Δ $MM' : bucket.scaledLevers.map((lever) => LEVER_LABELS[lever]).join(' + ')}
                      {bucket.carriesOverlays ? ' + driver overlays' : ''}
                      {': '}
                      {ADJUSTMENT_YEARS.map((year, yearIndex) =>
                        `FY${String(year).slice(2)} ${aggregate[yearIndex] >= 0 ? '+' : ''}${(aggregate[yearIndex] || 0).toFixed(1)}`
                      ).join(' · ')}
                    </span>
                  )}
                </div>

                {isOpen && (
                  <div className="forecast-home-table-wrap">
                    <table className="forecast-home-table op3-year-table aligned">
                      <thead>
                        <tr>
                          <th>Unit</th>
                          <th>Weight</th>
                          <th>Share</th>
                          {hasDollars
                            ? ADJUSTMENT_YEARS.map((year) => <th key={year}>FY{year}</th>)
                            : <th>Hires assigned</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {weights.map(({ unit, weight, raw }) => (
                          <tr key={unit.id}>
                            <td>
                              <strong>{unit.division}</strong>
                              <span className="forecast-home-market"> · {unit.market}</span>
                            </td>
                            <td>
                              <input
                                type="number" className="op3-stepper" min={0} step={0.1}
                                value={overrides[`${bucket.id}:${unit.id}`] !== undefined
                                  ? overrides[`${bucket.id}:${unit.id}`]
                                  : Number(raw.toFixed(1))}
                                onChange={(event) =>
                                  setOverrides((previous) => ({
                                    ...previous, [`${bucket.id}:${unit.id}`]: event.target.value
                                  }))
                                }
                              />
                            </td>
                            <td className="forecast-mono">{(weight * 100).toFixed(1)}%</td>
                            {hasDollars ? (
                              ADJUSTMENT_YEARS.map((year, yearIndex) => {
                                const value = (aggregate[yearIndex] || 0) * weight;
                                return (
                                  <td key={year} className={`forecast-mono ${value >= 0 ? 'pos' : 'neg'}`}>
                                    {formatCell(bucket, value)}
                                  </td>
                                );
                              })
                            ) : (
                              <td className="forecast-mono">
                                {hireRows.filter((row) => hireAssignment[row.id] === unit.id).length} cohorts
                                {' · '}
                                {hireRows
                                  .filter((row) => hireAssignment[row.id] === unit.id)
                                  .reduce((sum, row) => sum + (Number(row.fte) || 0), 0)} FTE
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          <p className="forecast-submit-note">
            Each bucket spreads on its own driver: dollar levers and overlays pro-rate by the
            bucket's weights, growth points and workforce percentages copy to every unit, and hire
            cohorts go whole to the most under-weighted unit on balance share. Every unit receives
            an editable draft with the bucket weights recorded in lineage — open the unit to
            review, regenerate its cube rows, and submit.
          </p>

          <div className="forecast-submit-actions">
            {pushResult?.tone === 'ok' ? (
              <>
                <span className="forecast-health-chip green">
                  Allocated to {units.length} units — drafts saved
                </span>
                <button type="button" className="forecast-primary-button" onClick={() => navigate('/forecasting')}>
                  Back to cycle home
                </button>
              </>
            ) : (
              <button type="button" className="forecast-primary-button" disabled={pushing} onClick={pushDown}>
                <Send size={15} />
                {pushing ? 'Pushing allocations…' : `Push to ${units.length} units`}
              </button>
            )}
            {pushResult?.tone === 'error' && (
              <span className="forecast-health-chip red">{pushResult.message}</span>
            )}
          </div>
        </div>
      </details>
    </section>
  );
};

export default ForecastAllocation;
